// Selling a Service articles for the Help Center.

const selling = {
  slug: 'selling',
  label: 'Selling a Service',
  icon: 'PiBriefcase',
  description: 'How to list a service, agree on terms, and get paid for your work.',
  articles: [
    {
      slug: 'creating-a-listing',
      title: 'Creating a listing',
      summary: 'Set a title, category, description, and at least one package.',
      body: [
        {
          type: 'p',
          text: 'To offer a service, go to Post a Service and walk through the studio. You will give your listing a title, choose a category and subcategory, write a description, and add photos or a video if you have them. Listings with real photos and a clear description get far more interest than bare bones ones.',
        },
        {
          type: 'steps',
          items: [
            'Go to Post a Service.',
            'Choose a category and subcategory that best match what you are offering.',
            'Write a clear title and description of what you do.',
            'Add at least one package with a price.',
            'Fill in your service agreement terms.',
            'Open the listing.',
          ],
        },
        {
          type: 'note',
          text: 'A listing stays in draft until it has everything it needs, including a signed service agreement template. You will see exactly what is missing before you can open it.',
        },
      ],
    },
    {
      slug: 'writing-your-listing-with-ai',
      title: 'Writing your listing with AI',
      summary: 'Draft your title, description, or FAQ answers from a prompt, then edit before you use it.',
      body: [
        {
          type: 'p',
          text: 'While you are building a listing, look for the Write with AI button next to the title, description, and FAQ answer fields. Tell it anything you want it to focus on, or leave that blank and it will work from what is already on the form plus your own profile.',
        },
        {
          type: 'p',
          text: 'It only ever produces a draft. Nothing is added to your listing until you choose Use this, and you can regenerate it as many times as you want, or discard it and write the field yourself instead.',
        },
        {
          type: 'note',
          text: 'It never invents a price, a delivery date, or a promise you have not already made elsewhere on the listing.',
        },
      ],
    },
    {
      slug: 'setting-your-packages-and-pricing',
      title: 'Setting your packages and pricing',
      summary: 'Offer up to three tiers so buyers can pick what fits their budget.',
      body: [
        {
          type: 'p',
          text: 'Every listing needs at least a Basic package. You can add Standard and Premium tiers above it if you want to offer more at a higher price. Each package has its own price, list of what is included, delivery time, and number of revisions.',
        },
        {
          type: 'p',
          text: 'Price your work based on the time and effort it actually takes. You can always edit your packages later if you find you are pricing too low or too high.',
        },
        {
          type: 'note',
          text: 'You can offer unlimited revisions on a package instead of a fixed number, if that fits how you work. It shows up to buyers exactly as "Unlimited."',
        },
      ],
    },
    {
      slug: 'writing-your-service-agreement',
      title: 'Writing your service agreement',
      summary: 'Your standing terms, used as the starting point for every order.',
      body: [
        {
          type: 'p',
          text: 'Every listing needs a service agreement template before it can go live. This is where you set your standard terms: what happens with revisions beyond the ones included, who owns the finished work, whether the project involves anything confidential, and any other conditions you want in writing.',
        },
        {
          type: 'p',
          text: 'This template is the starting point for every order on this listing, but it is not automatically binding. Each time someone requests an order, you still review and sign that specific agreement yourself before it becomes final. That way you always get a chance to look at what the buyer actually described before committing.',
        },
      ],
    },
    {
      slug: 'the-gig-workflow-checklist',
      title: 'The gig workflow checklist',
      summary: 'An optional list of steps you follow for every order, shown to the buyer too.',
      body: [
        {
          type: 'p',
          text: 'If your work follows a repeatable process, for example a discovery call, a first draft, and a final review, you can add those steps to your listing. Buyers see this same checklist as soon as they place an order, so they know what to expect and when.',
        },
        {
          type: 'p',
          text: 'While an order is active, you can check off each step as you complete it. Once every step is checked, you will be prompted to deliver the finished work.',
        },
      ],
    },
    {
      slug: 'responding-to-a-service-request',
      title: 'Responding to a service request',
      summary: 'Review what the buyer needs, then accept or decline the agreement.',
      body: [
        {
          type: 'p',
          text: 'When a buyer messages you and requests a service agreement, you will see it in that conversation along with their message describing what they need. Read it over. If it matches what you can deliver, sign your side to accept. If it does not, you can decline instead of accepting something you cannot actually do.',
        },
        {
          type: 'p',
          text: 'Declining does not charge anyone anything. It closes that request, and the buyer can message you again with an adjusted scope if they want to try again.',
        },
        {
          type: 'note',
          text: 'Payment only becomes possible once you have accepted and both sides have signed. Nothing is charged to the buyer before that.',
        },
      ],
    },
    {
      slug: 'delivering-work',
      title: 'Delivering work',
      summary: 'Upload your files and a short note once you are done.',
      body: [
        {
          type: 'p',
          text: 'Once an order is active and funded, you can deliver the finished work from that conversation or from the Orders page. Attach your files and add a short note describing what you are handing over.',
        },
        {
          type: 'p',
          text: 'The buyer will be notified and can approve the delivery, which releases your payment, or request a revision if their package includes any left. They have 7 days to respond, and we remind them with 3 days left and on the last day. If they still do not respond, the order completes automatically and you are paid.',
        },
      ],
    },
    {
      slug: 'shipping-a-physical-item',
      title: 'Shipping a physical item',
      summary: 'Set a flat shipping price and mark the order shipped once it is posted.',
      body: [
        {
          type: 'p',
          text: 'If you list something under Physical Products, you set a flat shipping price for anywhere in the country when you create the listing. You can offer free shipping by entering zero. This price is added to the buyer\'s total automatically and is paid to you in full, with no commission taken on shipping.',
        },
        {
          type: 'p',
          text: 'The buyer provides their delivery address as part of paying, so you will see it on the order once it is funded. Once you have packed and posted the item, mark the order as shipped: pick the carrier and enter the tracking number from your postage receipt. The buyer gets a link to follow it. You can also add a photo of the receipt or the packed parcel, which settles most "where is it?" questions straight away.',
        },
        {
          type: 'p',
          text: 'Handing it over in person, or posting it without tracking? Tick "No tracking" instead. That is allowed, but you carry the risk: if it still has not reached the buyer 7 days after you post it and they report it as never arrived, they are refunded in full automatically.',
        },
        {
          type: 'note',
          text: 'Marking an order shipped is treated the same way delivering digital work is. The buyer has 10 days to confirm it arrived, and payment releases automatically if they do not respond. If the parcel is slow, the buyer can add 7 more days once.',
        },
      ],
    },
    {
      slug: 'proposing-a-change-to-an-active-order',
      title: 'Proposing a change to an active order',
      summary: 'Ask the buyer to agree to a different scope, timeline, or revision count mid-order.',
      body: [
        {
          type: 'p',
          text: 'If the work needs to change after an order is already active, for example the buyer wants something bigger than what was originally agreed, either side can propose a change from that order: a new price, delivery time, deliverables or revisions, plus a note. The other person accepts or declines it. Once accepted, the order itself updates. A higher price takes effect once the buyer pays the difference, and a lower one refunds the difference to them.',
        },
        {
          type: 'p',
          text: 'Nothing changes until the other person accepts. If they decline, the original agreement stays exactly as it was.',
        },
      ],
    },
    {
      slug: 'if-something-goes-wrong-with-an-order',
      title: 'If something goes wrong with an order',
      summary: 'If a buyer opens a review, reply to it. Silence is decided against you.',
      body: [
        {
          type: 'p',
          text: 'Most orders wrap up with a straightforward approval. If a buyer disagrees about whether delivered work meets what was agreed, they can open a review on the order. Reviews are opened by the buyer, not by you, and only once you have had a real chance to deliver: either something has been handed over or posted, or the delivery window on the package has run out. Opening one pauses the order so it cannot auto-complete while it is being looked at.',
        },
        {
          type: 'p',
          text: 'When a review is opened you have three days to reply with your side, on the order itself, and you can attach proof: exports, screenshots of what you sent, a tracking receipt. Use it. A review is decided by a person reading both accounts, and if you do not reply it is decided on the buyer\u2019s account alone.',
        },
        {
          type: 'p',
          text: 'A review is resolved by releasing payment to you, refunding the buyer, or splitting it between you. On a physical order, a split never claws back the postage you already paid: that passes through to you whole. If the two of you sort it out in messages first, the buyer can withdraw the review and the order simply carries on.',
        },
        {
          type: 'note',
          text: 'If you never delivered anything and never reply to the review, it refunds the buyer in full automatically once the reply window closes. That case needs no judgement call.',
        },
      ],
    },
  ],
};

export default selling;
